import { useEffect, useRef } from "react";

type EdgeSwipeDrawerOptions = {
  drawerOpen: boolean;
  onOpen: () => void;
  enabled?: boolean;
  edgeWidth?: number;
  minDistance?: number;
  maxVerticalDrift?: number;
};

type ActiveGesture = {
  direction: 1 | -1;
  pointerId: number;
  startX: number;
  startY: number;
};

const MOBILE_DRAWER_QUERY = "(max-width: 1023px)";
const DEFAULT_EDGE_WIDTH = 24;
const DEFAULT_MIN_DISTANCE = 64;
const DEFAULT_MAX_VERTICAL_DRIFT = 36;

export function useMobileEdgeSwipeDrawer({
  drawerOpen,
  edgeWidth = DEFAULT_EDGE_WIDTH,
  enabled = true,
  maxVerticalDrift = DEFAULT_MAX_VERTICAL_DRIFT,
  minDistance = DEFAULT_MIN_DISTANCE,
  onOpen
}: EdgeSwipeDrawerOptions) {
  const callbacksRef = useRef({ drawerOpen, onOpen });
  const activeGestureRef = useRef<ActiveGesture | null>(null);

  useEffect(() => {
    callbacksRef.current = { drawerOpen, onOpen };
  }, [drawerOpen, onOpen]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    function isMobileViewport(): boolean {
      return window.matchMedia?.(MOBILE_DRAWER_QUERY).matches ?? window.innerWidth <= 1023;
    }

    function onPointerDown(event: PointerEvent) {
      if (!isMobileViewport() || callbacksRef.current.drawerOpen) return;
      if (event.isPrimary === false) return;
      if (event.button !== undefined && event.button !== 0) return;
      if (isInsideHorizontalScroller(event.target)) return;

      const isRtl = document.documentElement.dir === "rtl";
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const startsAtDrawerEdge = isRtl
        ? viewportWidth - event.clientX <= edgeWidth
        : event.clientX <= edgeWidth;

      if (!startsAtDrawerEdge) return;

      activeGestureRef.current = {
        direction: isRtl ? -1 : 1,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY
      };
    }

    function onPointerMove(event: PointerEvent) {
      const gesture = activeGestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      const inlineDistance = (event.clientX - gesture.startX) * gesture.direction;
      const verticalDrift = Math.abs(event.clientY - gesture.startY);

      if (inlineDistance < -8 || (verticalDrift > maxVerticalDrift && verticalDrift > Math.abs(inlineDistance))) {
        activeGestureRef.current = null;
        return;
      }

      if (inlineDistance >= minDistance && verticalDrift <= maxVerticalDrift) {
        callbacksRef.current.onOpen();
        activeGestureRef.current = null;
      }
    }

    function clearGesture(event: PointerEvent) {
      const gesture = activeGestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      activeGestureRef.current = null;
    }

    function onDragStart(event: DragEvent) {
      // If the swipe starts over (or near) an existing text selection, the
      // browser turns the drag into native selection drag-and-drop and stops
      // delivering pointermove/pointerup entirely — the gesture silently
      // dies mid-swipe (WebKit is strictest, but the DnD semantics are
      // cross-engine). While an edge gesture is armed, the swipe wins.
      if (activeGestureRef.current) event.preventDefault();
    }

    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", clearGesture, { passive: true });
    window.addEventListener("pointercancel", clearGesture, { passive: true });
    window.addEventListener("dragstart", onDragStart);

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", clearGesture);
      window.removeEventListener("pointercancel", clearGesture);
      window.removeEventListener("dragstart", onDragStart);
    };
  }, [edgeWidth, enabled, maxVerticalDrift, minDistance]);
}

function isInsideHorizontalScroller(target: EventTarget | null): boolean {
  let element = target instanceof Element ? target : null;
  while (element && element !== document.body) {
    if (element.closest("[data-edge-swipe-ignore='true']")) return true;
    const style = window.getComputedStyle(element);
    const scrollsInline =
      (style.overflowX === "auto" || style.overflowX === "scroll") &&
      element.scrollWidth > element.clientWidth;
    if (scrollsInline) return true;
    element = element.parentElement;
  }
  return false;
}
